<template>
  <Dialog :open="open" @update:open="$emit('update:open', $event)">
    <DialogContent class="max-w-[450px] rounded-xl p-0" @pointerDownOutside.prevent @escapeKeyDown.prevent>
      <div v-if="isLoading" class="flex items-center justify-center p-12">
        <Loader2 class="h-8 w-8 animate-spin text-brand-600" />
      </div>

      <BoostCancellationChoice
        v-else-if="view === views.CHOICE"
        :merchant-name="order?.merchantName ?? ''"
        :is-threshold-met="isThresholdMet"
        @cash-refund="view = views.POLICY"
        @redeem-voucher="onRedeemVoucher"
      />

      <BoostCancellationPolicy
        v-else-if="view === views.POLICY"
        v-model:selected-reason="selectedReason"
        v-model:agreed-to-policy="agreedToPolicy"
        :refund-before-deductions="refundBeforeDeductions"
        :cancellation-fee="cancellationFee"
        :reward-amount="rewardAmount"
        :refund-after-deductions="refundAfterDeductions"
        :is-threshold-met="isThresholdMet"
        :cancellation-reasons="cancellationReasons"
        @continue="view = views.CONFIRM"
        @redeem="onRedeemVoucher"
        @close="close"
      />

      <ConfirmAction
        v-else-if="view === views.CONFIRM"
        title="Cancel this order?"
        description="By selecting 'YES' you acknowledge that this action cannot be reversed."
        :is-submitting="isSubmitting"
        @confirm="confirmCancellation"
        @cancel="close"
      />

      <div v-else-if="view === views.SUCCESS" class="p-6">
        <div class="flex flex-col items-center text-center">
          <SuccessIcon />
          <h3 class="text-[18px] font-bold text-grey-light-900 mb-2 mt-4">Your order has been cancelled</h3>
          <p class="text-[14px] text-grey-light-600 mb-6">
            We're sorry to see you cancel your order, but we hope to see you soon again!
          </p>
          <Button class="w-full bg-teal-600 hover:bg-teal-700" @click="close">Done</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <SuccessErrorModal
    :show-modal="showErrorModal"
    title="Error"
    description="Failed to process cancellation. Please try again."
    :is-error="true"
    @close="showErrorModal = false"
  />
</template>

<script setup lang="ts">
import SuccessErrorModal from '@/components/common/SuccessErrorModal.vue';
import SuccessIcon from '@/components/icons/SuccessIcon.vue';
import BoostCancellationChoice from '@/components/shopper/boost-rewards/cancel-boost/BoostCancellationChoice.vue';
import BoostCancellationPolicy from '@/components/shopper/boost-rewards/cancel-boost/BoostCancellationPolicy.vue';
import ConfirmAction from '@/components/shopper/lay-by/cancellation-request/ConfirmAction.vue';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { NetworkError } from '@/errors';
import { getOrderCancellationPreviewFee, updateOrder } from '@/services/orders';
import { useUserStore } from '@/stores/users';
import dayjs from '@/utils/dayjs.utils';
import { formatDateMedium } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { Loader2 } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';

type View = 'choice' | 'policy' | 'confirm' | 'success';

const views = {
  CHOICE: 'choice',
  POLICY: 'policy',
  CONFIRM: 'confirm',
  SUCCESS: 'success',
} as const;

const props = defineProps<{
  open: boolean;
  order?: ModelOrderResponse;
  paidAmount: number;
  rewardAmount: number;
  isThresholdMet: boolean;
}>();

const emit = defineEmits<{
  'update:open': [value: boolean];
  cancelled: [];
  'open-redeem': [];
}>();

const { user } = useUserStore();

const view = ref<View>(views.CHOICE);
const selectedReason = ref('');
const agreedToPolicy = ref(false);
const isLoading = ref(false);
const isSubmitting = ref(false);
const cancellationFee = ref(0);
const showErrorModal = ref(false);

const cancellationReasons = [
  "Can't afford to continue payments",
  'Decided not to continue saving',
  'Found a better deal elsewhere',
  'Other',
];

const refundBeforeDeductions = computed(() => props.paidAmount);
const refundAfterDeductions = computed(() => Math.max(0, refundBeforeDeductions.value - cancellationFee.value));

const close = () => emit('update:open', false);

const onRedeemVoucher = () => {
  emit('open-redeem');
  close();
};

const confirmCancellation = async () => {
  if (!props.order?._id) return;
  isSubmitting.value = true;
  try {
    const notifications = [...(props.order.notifications ?? [])];
    notifications.push({
      change: 'ACCEPTED',
      date: formatDateMedium(dayjs().toISOString()),
      name: user?.name ?? '',
      reason: selectedReason.value,
      details: `Order cancelled by user: ${selectedReason.value}`,
      read: false,
      userId: user?._id,
      type: 'CANCELLATION',
    });

    await updateOrder(props.order._id, {
      state: 'CANCELLING',
      orderCancellationRequest: 'ACCEPTED',
      acceptedProposedCancellationTerms: true,
      notifications,
    });

    addMetric('cancellation_request', { status: 'success', channel: 'shopper', action: 'boost_cancel' });
    view.value = views.SUCCESS;
    emit('cancelled');
  } catch (error: any) {
    showErrorModal.value = true;
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('cancellation_request', { status: 'network_error', channel: 'shopper', action: 'boost_cancel' });
    } else {
      createException(error);
      addMetric('cancellation_request', { status: 'error', channel: 'shopper', action: 'boost_cancel' });
    }
  } finally {
    isSubmitting.value = false;
  }
};

watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      view.value = views.CHOICE;
      selectedReason.value = '';
      agreedToPolicy.value = false;
      isSubmitting.value = false;
      isLoading.value = true;

      try {
        if (props.order?._id) {
          cancellationFee.value = await getOrderCancellationPreviewFee(props.order._id);
        }
      } catch (error) {
        createException(error);
        showErrorModal.value = true;
        cancellationFee.value = 0;
      } finally {
        isLoading.value = false;
      }
    }
  },
);
</script>
